import {
    AccumulatedResults,
    ExploreSideBarLayout,
    FilterBar,
    FilterBarOpenButton,
    SearchBar,
    SearchResultsContainerWithLoadMore,
} from "@kaltura/mediaspace-shared-search";
import {
    baseUrl,
    extractDefaultFilterValues,
    removeEmptyDefaultFilterValues,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FeatureContainer, Stack, systemWidth, useTheme } from "@kaltura/mediaspace-shared-styled";
import {
    BaseEntryResultType,
    FilterProps,
    FilterValues,
    KmsTypeComponent,
    SupportedKmsTypeFilter,
} from "@kaltura/mediaspace-shared-types";
import { buildQuerystring } from "kaltura-typescript-client/adapters/utils";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import styled from "@emotion/styled";
import { Typography } from "@kaltura/ds-react-components";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";
import {
    HeaderBaseProps,
    MediaCardContext,
    SearchResultsDataWithLoadMore,
    SearchResultsSkeleton,
} from "@kaltura/mediaspace-shared-media-list";
import { SpanSrOnly } from "@kaltura/mediaspace-shared-ui";
import { Header } from "./header/Header";
import { NoHistory } from "./NoHistory";
import { MyHistoryMediaCard } from "./media-card/MyHistoryMediaCard";
import { KalturaViewHistoryUserEntry } from "kaltura-typescript-client/api/types/KalturaViewHistoryUserEntry";
import { KalturaMediaEntry } from "kaltura-typescript-client/api/types/KalturaMediaEntry";

export type ProgressBarConfig = {
    endSeconds: number;
    endPercent: number;
};

export interface MyHistoryProps {
    filters: FilterProps<SupportedKmsTypeFilter>[];
    initialValues?: FilterValues;
    pageSize: number;
    progressBarConfig: ProgressBarConfig;
}

/**
 * Extended Kaltura types to include userEntry property that exists at runtime
 * but is not defined in the base Kaltura TypeScript types.
 * Using intersection types to add the userEntry property without breaking compatibility.
 */
export type KalturaBaseEntryWithUserEntry = KalturaMediaEntry & {
    userEntry: KalturaViewHistoryUserEntry;
};

type KalturaESearchEntryResultWithUserEntry = Omit<KalturaESearchEntryResult, "object"> & {
    object: KalturaBaseEntryWithUserEntry;
};

type MyHistoryAccumulatedResults = AccumulatedResults<KalturaESearchEntryResultWithUserEntry>;

const baseClassName = "kms-ds-my-history";

/**
 * My History main component
 */
export const MyHistory = (props: MyHistoryProps) => {
    const { filters, initialValues = {}, pageSize, progressBarConfig } = props;

    // filter values as the user set them (+ defaults)
    const [filterValues, setFilterValues] = useState(() => {
        return { ...extractDefaultFilterValues(filters), ...initialValues };
    });

    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [selectedSearchResult, setSelectedSearchResult] = useState<BaseEntryResultType>();

    const theme = useTheme();
    const mdScreen = useMediaQuery(theme.breakpoints.up("md"));
    const lgDownScreen = useMediaQuery(theme.breakpoints.down("lg"));

    const filtersButtonRef = useRef<HTMLButtonElement>(null);

    // reset filter modal state
    useEffect(() => {
        setIsFiltersModalOpen((isFiltersModalOpen) => isFiltersModalOpen && !mdScreen);
    }, [mdScreen]);

    // handle filter change
    const handleFilterChange = (values: FilterValues) => {
        setFilterValues(values);
        setSelectedSearchResult(undefined);
    };

    // handle submit search form
    const handleKeywordSearchSubmit = (value: string) => {
        setFilterValues((filterValues) => ({
            ...filterValues,
            keyword: value,
        }));
        setSelectedSearchResult(undefined);
    };

    // minimal filter values - no empties, no defaults
    // will be recalculated on filters or filterValues change
    const minFilterValues = useMemo(
        () => removeEmptyDefaultFilterValues(filters, filterValues),
        [filters, filterValues]
    );

    // update history on filter change
    useEffect(() => {
        const updateHistory = (filterValues: FilterValues) => {
            // update query string
            const queryString = buildQuerystring(filterValues);
            window.history.pushState(null, "", `?${queryString}`);
        };

        updateHistory(minFilterValues);
    }, [filters, minFilterValues]);

    // hide filters (show in modal) in md size when result selected
    const modalFilters = selectedSearchResult && lgDownScreen;

    // check from context if this is kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    // on screens smaller than md, show button to open filters modal
    let searchBarButtons;
    if (!mdScreen || modalFilters) {
        searchBarButtons = [<FilterBarOpenButton ref={filtersButtonRef} onClick={() => setIsFiltersModalOpen(true)} />];
    }

    const header: KmsTypeComponent<any, HeaderBaseProps> = {
        component: Header,
        name: "MyHistoryHeader",
        props: {},
    };

    const mediaCard: KmsTypeComponent = {
        component: MyHistoryMediaCard,
        name: "MyHistoryMediaCard",
        props: { progressBarConfig },
    };

    // Custom pager function to calculate next page parameters using 'until' instead of 'page'
    // Calculates the timestamp for the next page of results in a time-based pagination.
    // It reduces the 'until' value to the oldest fetched record's time minus one millisecond.
    const getNextPageParams = (
        accumulatedResults: AccumulatedResults<KalturaESearchEntryResult>,
        filterValues: FilterValues | undefined,
        pageSize: number
    ) => {
        // Type assertion: The server returns data with userEntry property for My History
        const typedResults = accumulatedResults as any as MyHistoryAccumulatedResults;
        const objects = typedResults?.entryResults?.objects;
        // If there are no accumulated objects, do not attempt to calculate a new `until` value.
        if (!objects || objects.length === 0) {
            return {
                ...filterValues,
                pageSize,
            };
        }
        const lastIndex = objects.length - 1;
        // Decrement the oldest timestamp by 1 to avoid fetching the same record again.
        const until = objects[lastIndex].object.userEntry.updatedAt - 1;

        return {
            ...filterValues,
            until,
            pageSize,
        };
    };

    return (
        <MyHistoryContainer className={baseClassName}>
            <Stack direction={"column"} gap={3}>
                <TitleContainer>
                    <Title variant="h2" component="h1">
                        {translate("My History")}
                    </Title>
                </TitleContainer>

                <ContentContainer
                    className={`${baseClassName}-content-container`}
                    onClose={() => setSelectedSearchResult(undefined)}
                    searchResult={selectedSearchResult}
                    filterValues={minFilterValues}
                    mediaContext={"myhistory"}
                    metaDataFetchUrl={`${baseUrl}/history-ds/explore-entry/entryId/${selectedSearchResult?.object.id}`}
                >
                    <ContentFiltersContainer inModal={modalFilters}>
                        <FiltersContainer inModal={modalFilters} className={`${baseClassName}-filters-container`}>
                            <SearchFormContainer
                                inModal={modalFilters}
                                className={`${baseClassName}-search-form-container`}
                            >
                                <SearchBar
                                    buttons={searchBarButtons}
                                    keywordSearchProps={{
                                        onSubmit: handleKeywordSearchSubmit,
                                        value: filterValues["keyword"] as string,
                                        placeholder: translate("Search in My History"),
                                        ariaLabel: translate("Search in My History"),
                                    }}
                                />
                            </SearchFormContainer>

                            <FilterBar
                                filters={filters}
                                values={filterValues}
                                onChange={handleFilterChange}
                                isModalOpen={isFiltersModalOpen}
                                inModal={modalFilters}
                                onModalClose={() => {
                                    setIsFiltersModalOpen(false);
                                    // when closing filters modal, focus the button that opened it:
                                    filtersButtonRef.current?.focus();
                                }}
                                resultsContainerId="search-results"
                            />
                        </FiltersContainer>
                        <MediaCardContext.Provider value={{ openLinksInNewTab: !isKaf, displayActionsInline: true }}>
                            <ResultsContainer
                                id="search-results"
                                tabIndex={-1}
                                role="region"
                                aria-labelledby="search-results-label"
                                className={`${baseClassName}-results-container`}
                            >
                                <SpanSrOnly id="search-results-label">{translate("Search results")}</SpanSrOnly>
                                <SearchResultsContainerWithLoadMore
                                    searchUrl={`${baseUrl}/history-ds/search-results`}
                                    isWideLayout={!selectedSearchResult}
                                    filterValues={minFilterValues}
                                    pageSize={pageSize}
                                    onEntryShowMore={(item) => setSelectedSearchResult(item)}
                                    searchResultsDisplayComponent={SearchResultsDataWithLoadMore}
                                    searchResultsSkeletonComponent={SearchResultsSkeleton}
                                    getNextPageParams={getNextPageParams}
                                    displayComponentProps={{
                                        noMediaComponent: NoHistory,
                                        header,
                                        mediaCard,
                                        additionalInvalidation: () => setSelectedSearchResult(undefined),
                                    }}
                                />
                            </ResultsContainer>
                        </MediaCardContext.Provider>
                    </ContentFiltersContainer>
                </ContentContainer>
            </Stack>
        </MyHistoryContainer>
    );
};

export default MyHistory;

const MyHistoryContainer = styled(FeatureContainer)(({ theme }) => ({
    ...systemWidth({ theme }),
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
}));

const TitleContainer = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
        marginTop: theme.spacing(3),
    },
}));

const Title = styled(Typography)({
    margin: 0,
});

const ContentContainer = styled(ExploreSideBarLayout)(({ theme }) => ({
    flexGrow: 1,
    [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(1),
    },
}));

const ContentFiltersContainer = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "inModal";
        },
    }
)<{ inModal?: boolean }>(({ theme, inModal }) => ({
    display: "flex",
    flexDirection: inModal ? "column" : "row",
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
    },
}));

const FiltersContainer = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "inModal";
        },
    }
)<{ inModal?: boolean }>(({ theme, inModal }) => ({
    width: inModal ? "100%" : 205,
    marginRight: theme.spacing(4),
    flexGrow: 0,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        width: "100%",
    },
}));

const SearchFormContainer = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "inModal";
        },
    }
)<{ inModal?: boolean }>(({ inModal, theme }) => ({
    marginBottom: inModal ? 0 : theme.spacing(3),
    [theme.breakpoints.down("md")]: {
        marginBottom: 0,
    },
}));

const ResultsContainer = styled("div")({
    flex: "1 1 0",
    minWidth: 0,
    "&:focus-visible": VisibleFocusStyle,
});
